import axios from 'axios'
import env from 'lib/env'
import { cookies, DOMAIN_COOKIE } from 'lib/cookies'
import { KEY_USER_STORAGE } from 'utils/constants'

interface JwtClaims {
  exp?: number
  iat?: number
}

interface RefreshResponse {
  idToken: string
  accessToken?: string
  refreshToken?: string
}

const REFRESH_URL = `${env.api.v3}/auth/refresh-token`
const REFRESH_LOCK = 'jobs-session-refresh'
const REFRESH_MARGIN_MS = 30 * 1000
const REFRESH_TIMEOUT_MS = 10 * 1000
// Prazo padrão do jobs-auth quando o refresh token não é um JWT
const FALLBACK_EXPIRES_DAYS = 2

let renewing: Promise<string | undefined> | null = null
const sessionEndListeners = new Set<() => void>()

const decodeJwt = (token = ''): JwtClaims => {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    return JSON.parse(atob(payload)) || {}
  } catch {
    return {}
  }
}

const isUsable = (idToken?: string, rejected?: string) => {
  const { exp } = decodeJwt(idToken)
  return (
    !!idToken &&
    idToken !== rejected &&
    typeof exp === 'number' &&
    exp * 1000 - Date.now() > REFRESH_MARGIN_MS
  )
}

// Duração do refresh token (exp - iat) pelo relógio local, como no jobs-auth
const getSessionExpires = (refreshToken: string): Date | number => {
  const { exp, iat } = decodeJwt(refreshToken)
  if (typeof exp !== 'number' || typeof iat !== 'number' || exp <= iat) {
    return FALLBACK_EXPIRES_DAYS
  }

  return new Date(Date.now() + (exp - iat) * 1000)
}

const saveSession = (session: RefreshResponse) => {
  const expires = getSessionExpires(session.refreshToken)

  cookies.set('idToken', session.idToken, { expires })
  cookies.set('refreshToken', session.refreshToken, { expires })
  if (session.accessToken) {
    cookies.set('accessToken', session.accessToken, { expires })
  }
}

const isRejectedByServer = (err: unknown) => {
  const status = axios.isAxiosError(err) ? err.response?.status : undefined
  return status === 400 || status === 401
}

export const onSessionEnd = (listener: () => void) => {
  sessionEndListeners.add(listener)

  return () => {
    sessionEndListeners.delete(listener)
  }
}

export const endSession = () => {
  if (cookies.get('idToken')) sessionStorage.removeItem(KEY_USER_STORAGE)

  Object.keys(cookies.get()).forEach(name =>
    cookies.remove(name, DOMAIN_COOKIE)
  )
  sessionEndListeners.forEach(listener => listener())
}

async function renewSession(rejected?: string): Promise<string | undefined> {
  // Outra aba pode ter renovado enquanto esta aguardava o lock
  const current = cookies.get('idToken')
  if (isUsable(current, rejected)) return current

  const refreshToken = cookies.get('refreshToken')
  if (!refreshToken) {
    endSession()
    return undefined
  }

  try {
    const { data } = await axios.post<RefreshResponse>(
      REFRESH_URL,
      { refreshToken },
      { timeout: REFRESH_TIMEOUT_MS }
    )
    if (!data?.idToken) throw new Error('Resposta do refresh sem idToken')

    saveSession({ ...data, refreshToken: data.refreshToken || refreshToken })
    return data.idToken
  } catch (err) {
    // Outro app no mesmo domínio pode ter renovado durante a chamada
    const latest = cookies.get('idToken')
    if (latest !== current && isUsable(latest, rejected)) return latest

    if (isRejectedByServer(err)) {
      endSession()
      return undefined
    }

    // Falha transitória (rede, timeout, 5xx): mantém a sessão
    return current
  }
}

const withRefreshLock = async <T>(task: () => Promise<T>): Promise<T> =>
  typeof navigator !== 'undefined' && navigator.locks
    ? navigator.locks.request(REFRESH_LOCK, task)
    : task()

// idToken pronto para uso. Renova a sessão quando o token está perto de vencer
// ou quando a API acabou de recusá-lo. Sem sessão, resolve undefined.
export const getValidIdToken = async (rejected?: string) => {
  const current = cookies.get('idToken')
  if (isUsable(current, rejected)) return current
  if (!current && !cookies.get('refreshToken')) return undefined

  // Um único refresh por vez: chamadas paralelas aguardam a mesma renovação
  renewing ??= withRefreshLock(() => renewSession(rejected)).finally(() => {
    renewing = null
  })

  return renewing
}
